<template>
  <div
    v-if="deluge.canvaspro_core_info?.product_name"
    class="fullscreen-container">
    <v-window v-model="window">
      <v-window-item>
        <div class="inner-container">
          <CProHeader
            header="Welcome to CanvasPro"
            subHeader="Enter the following details to get started" />
          <v-text-field
            filled
            bg-color="var(--v-fieldColor-base) !important"
            color="var(--text) !important"
            style="width: 300px"
            v-model="cpro_name"
            :label="
              'Name your CanvasPro ' +
              deluge.canvaspro_core_info?.product_name.charAt(0).toUpperCase() +
              deluge.canvaspro_core_info?.product_name.slice(1)
            "
            v-on:keyup.enter="window = 1"></v-text-field>
          <v-text-field
            v-model="cpro_admin_email"
            filled
            style="width: 300px"
            bg-color="var(--v-fieldColor-base) !important"
            color="var(--text) !important"
            label="Set admin email address"
            type="email"
            v-on:keyup.enter="window = 1"></v-text-field>
          <v-text-field
            v-model="cpro_admin_pass"
            filled
            style="width: 300px"
            bg-color="var(--v-fieldColor-base) !important"
            color="var(--text) !important"
            label="Set admin password"
            type="password"
            v-on:keyup.enter="window = 1"></v-text-field>
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            style="width: 300px"
            class="mb-4">
            A branded desktop wallpaper will be applied with the machine name.
            To show IP addresses and a QR code on it, enable this under
            Configure &gt; Desktop or select the option during installation.
          </v-alert>
          <v-btn
            color="primary"
            style="width: 300px"
            @click="window = 1"
            :loading="loading"
            :disabled="
              !cpro_name ||
              !cpro_admin_pass ||
              !cpro_admin_email ||
              !valid_email(cpro_admin_email)
            "
            append-icon="mdi-arrow-right-circle">
            Next
          </v-btn>
        </div>
      </v-window-item>
      <v-window-item>
        <div class="inner-container eula">
          <CProHeader header="End User License Agreement" />
          <div
            style="
              color: white;
              height: 500px;
              overflow: auto;
              white-space: pre-wrap;
            ">
            {{ eula }}
          </div>

          <div class="button-box mt-4">
            <v-btn
              color="primary"
              class="button"
              @click="handleBack"
              prepend-icon="mdi-arrow-left-circle">
              Back
            </v-btn>
            <v-btn
              color="primary"
              class="button"
              @click="state.beta ? handleNext : complete()"
              :loading="loading"
              prepend-icon="mdi-check-circle"
              :disabled="
                !cpro_name || !cpro_admin_pass || !valid_email(cpro_admin_email)
              ">
              Accept
            </v-btn>
          </div>
        </div>
      </v-window-item>
      <v-window-item v-if="state.beta">
        <div class="inner-container graphics-setup">
          <CProHeader
            header="Graphics Setup"
            subHeader="Select your graphics card and display" />
          <FirstTimeGPU
            currentConfigName="CanvasPro"
            @setGPUS="(data) => (GPUS = data)"
            @selectedGPUAndOutput="handleGPU" />
          <div
            class="button-box"
            v-if="GPUS">
            <v-btn
              color="primary"
              @click="handleBack"
              prepend-icon="mdi-arrow-left-circle">
              Back
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              @click="
                GPUSkipped = false;
                complete();
              "
              append-icon="mdi-arrow-right-circle">
              Finish
            </v-btn>
          </div>
        </div>
      </v-window-item>
    </v-window>
  </div>
</template>
<script setup>
import CProHeader from './CProHeader.vue';
import FirstTimeGPU from './FirstTimeGPU.vue';

import { deluge, state } from '@/stores/deluge.js';
import { $http } from '../../stores/http.js';
import { computed, reactive, ref, watch } from 'vue';
import { updateConfig } from '../../utils/ventuzConfig';
import eula from '../../../license.txt?raw';

const emit = defineEmits(['complete', 'respObj']);

let respObj = {};
const cpro_name = ref(deluge.canvaspro_core_info?.domain);
const cpro_admin_pass = ref(undefined);
const cpro_admin_email = ref(undefined);

const GPUS = ref([]);
const GPUSkipped = ref(null);
const gpuNextDisabled = ref(true);

const selectedGpusAndOutput = reactive({
  gpu_index: null,
  jack_port: null,
});

const gpuAndOutput = computed(() => {
  const gpu = GPUS.value.filter(
    (gpu) => gpu.template.index === selectedGpusAndOutput.gpu_index
  )[0];
  const output = gpu?.template.streams.filter(
    (stream) => stream.template.jacks === selectedGpusAndOutput.jack_port
  )[0];

  return { gpu, output };
});

const loading = ref(false);
const window = ref(0);

function handleGPU(data) {
  selectedGpusAndOutput.gpu_index = data.gpu_index;
  selectedGpusAndOutput.jack_port = data.jack_port;
  gpuNextDisabled.value = false;
}

function complete() {
  quick_start();
}

function handleBack() {
  window.value--;
}

function handleNext() {
  window.value++;
}

/**
 * @param {string} configName - Name of the current config.
 * @param {string} currentConfigName - Ref to update.
 */

async function quick_start() {
  loading.value = true;
  if (cpro_name.value || cpro_admin_pass.value || cpro_admin_email.value) {
    await save();
    await login();
    emit('respObj', respObj);

    if (gpuAndOutput.value.gpu || gpuAndOutput.value.output) {
      await setGPUAndOutput();
    }

    loading.value = false;
    emit('complete', respObj);
  }
}

async function setGPUAndOutput() {
  await $http('GET', `/dynamic_engine/device_config/reset`);
  const data = buildGPUAndOutputFromSelection(
    gpuAndOutput.value.gpu,
    gpuAndOutput.value.output
  );
  await updateConfig('CanvasPro', data);
}

function buildGPUAndOutputFromSelection(gpu, output) {
  if (!gpu || !output) {
    throw new Error(
      'buildGPUAndOutputFromSelection',
      'was called without an argument!'
    );
  }

  const data = {
    video: [
      {
        class: gpu.template.class,
        name: gpu.template.name,
        index: gpu.template.index,
        pcVideoNorms: gpu.template.pcVideoNorms,
        streams: [
          {
            name: output.template.name,
            mode: output.template.mode,
            jacks: output.template.jacks,
            videoRaster: output.template.videoRaster,
            videoSizeX: output.template.videoSizeX,
            videoSizeY: output.template.videoSizeY,
            options: output.template.options,
            synchronized: output.template.synchronized,
          },
        ],
      },
    ],
  };

  return data;
}

function valid_email(email) {
  let pattern = /\"?([-a-zA-Z0-9.`?{}]+@\w+\.\w+)\"?/;
  return pattern.test(email);
}
async function login() {
  return new Promise((resolve, reject) => {
    const FD = new FormData();
    var request = new XMLHttpRequest();
    FD.append('username', 'admin');
    FD.append('password', cpro_admin_pass.value);
    request.open('POST', state.devRedirect + '/api/token', FD);
    request.onload = () => {
      if (request.status !== 200) {
        alert('Username or password denied');

        reject({
          status: this.status,
          statusText: request.statusText,
          response: request.response,
        });
      }
      respObj = JSON.parse(request.responseText);
      if ('access_token' in respObj) {
        localStorage.setItem('username', 'admin');
        localStorage.setItem('authentication_token', respObj.access_token);
        resolve(request.response);
      }
    };
    request.onerror = () => {
      alert('Username or password denied');
    };
    request.send(FD);
  });
}
async function save() {
  try {
    await $http(
      'POST',
      '/initial_register',
      JSON.stringify({
        admin_pass: cpro_admin_pass.value,
        admin_email: cpro_admin_email.value,
        name: cpro_name.value,
        eula_accepted: true,
      })
    );
  } catch (error) {
    console.log(error);
  }
}
</script>

<style scoped>
.fullscreen-container {
  height: 100vh;
  height: 100dvh;
  height: -webkit-fill-available;
  height: calc(var(--vh, 1vh) * 100);
  width: 100vw;
  background: #1a1a1f;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  opacity: 1;
  z-index: 1000;
}
.inner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 600px;
}

.final-page {
  width: 300px;
}

.graphics-setup {
  width: 375px;
}
.eula {
  max-width: 600px;
  height: 100dvh;
  padding: 20px;
}
.gpu-loading {
  display: flex;
  gap: 2rem;
}

.button-box {
  margin-top: 24px;
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  width: 100%;
}

.button {
  width: 40%;
  max-width: 200px;
  min-width: 120px;
}
</style>
